import { useBusinessContext } from '@root/context'
import { CancelSubscriptionProps, cancelSubscription, HttpError } from '@root/query'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CancelFlow } from './CancelFlow'
import { FaMinusCircle } from 'react-icons/fa'
import styled from 'styled-components'
import toast from 'react-hot-toast'

export const CancelPlanButton: React.FC = () => {
  const { t } = useTranslation()
  const { slug } = useBusinessContext()
  const queryClient = useQueryClient()
  const [showFlow, setShowFlow] = React.useState(false)

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: ({
      disableEinvoicing,
      slug,
      churnReasons,
      churnReasonOther,
      keepAddons
    }: CancelSubscriptionProps) =>
      cancelSubscription({ slug, disableEinvoicing, churnReasons, churnReasonOther, keepAddons }),
    ...{
      onSuccess: async () => {
        await Promise.all([
          queryClient.removeQueries({ queryKey: [slug, 'features'] }),
          queryClient.removeQueries({ queryKey: [slug, 'integration_renew_info'] }),
          queryClient.removeQueries({ queryKey: ['background_fetch', slug] }),
          queryClient.invalidateQueries()
        ])
        setShowFlow(false)
      },
      onError: error => {
        const { status } = (error as unknown as HttpError) ?? {}
        if (status === 400) return
        toast.error(t('general.error'))
      }
    }
  })

  return (
    <>
      <StyledButton onClick={() => setShowFlow(true)}>
        <FaMinusCircle />
        <span>{t('settings.billing.cancelSubscription')}</span>
      </StyledButton>

      <CancelFlow
        isVisible={showFlow}
        onClose={() => setShowFlow(false)}
        onConfirm={({
          disableEinvoicing,
          slug,
          churnReasons,
          churnReasonOther,
          keepAddons
        }: CancelSubscriptionProps) =>
          mutateAsync({ disableEinvoicing, slug, churnReasons, churnReasonOther, keepAddons })
        }
        isSubmitting={isLoading}
      />
    </>
  )
}

const StyledButton = styled.button`
  outline: none !important;
  padding: none !important;
  border: none !important;
  margin: 0 !important;
  padding: 0 !important;
  background: transparent !important;

  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;

  &:hover {
    text-decoration: underline;
  }
`
